/**
 * QR kód člena. Mobilní aplikace ho čte v QRScreen.handleBarCodeScanned:
 * `JSON.parse(data)` a pak `parseInt(parsed.id)`. Nic víc z obsahu nečte.
 *
 * Pozor na Hostinu: recept se pozná podle toho, že má navíc `suroviny` a `body`.
 * Kód jen s `id` se tedy nikdy nezamění za recept.
 */
export function memberQrPayload(memberNumber: number): string {
  return JSON.stringify({ id: memberNumber });
}

/**
 * Rozparsuje výčet čísel: "1, 4, 7-9" → [1, 4, 7, 8, 9].
 * Vrací i chyby, aby šlo uživateli říct, čemu přesně nerozumíme.
 */
export function parseNumberList(input: string): { numbers: number[]; errors: string[] } {
  const numbers = new Set<number>();
  const errors: string[] = [];

  for (const rawPart of input.split(/[,;\s]+/)) {
    const part = rawPart.trim();
    if (!part) continue;

    const range = part.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (range) {
      const from = parseInt(range[1], 10);
      const to = parseInt(range[2], 10);
      if (from < 1 || to < 1) { errors.push(`${part}: čísla začínají od 1`); continue; }
      if (to < from) { errors.push(`${part}: konec je menší než začátek`); continue; }
      for (let n = from; n <= to; n++) numbers.add(n);
      continue;
    }

    if (/^\d+$/.test(part)) {
      const n = parseInt(part, 10);
      if (n < 1) { errors.push(`${part}: čísla začínají od 1`); continue; }
      numbers.add(n);
      continue;
    }

    errors.push(`${part}: nerozumím`);
  }

  return { numbers: [...numbers].sort((a, b) => a - b), errors };
}

/**
 * Vykreslí QR kód s číslem člena uprostřed.
 *
 * Jde to díky korekci chyb úrovně H, která snese 30 % poškození kódu. Překryv
 * zabírá 30 % šířky, tedy ~9 % plochy — s velkou rezervou. Kdyby se překryv
 * zvětšoval, je potřeba to znovu otestovat skenerem.
 */
export async function renderMemberQr(
  memberNumber: number,
  size: number,
  QRCode: typeof import('qrcode')
): Promise<string> {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, memberQrPayload(memberNumber), {
    width: size,
    margin: 1,
    errorCorrectionLevel: 'H',
    color: { dark: '#000000', light: '#FFFFFF' },
  });

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.toDataURL('image/png');

  const w = canvas.width;
  const box = Math.round(w * 0.3);
  const x = Math.round((w - box) / 2);
  const y = Math.round((w - box) / 2);
  const r = Math.round(box * 0.16);

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + box, y, x + box, y + box, r);
  ctx.arcTo(x + box, y + box, x, y + box, r);
  ctx.arcTo(x, y + box, x, y, r);
  ctx.arcTo(x, y, x + box, y, r);
  ctx.closePath();

  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.lineWidth = Math.max(2, Math.round(w * 0.006));
  ctx.strokeStyle = '#000000';
  ctx.stroke();

  const text = String(memberNumber);
  // Delší čísla se musí vejít do stejného pole, jinak by přetekla do kódu.
  const ratio = text.length <= 2 ? 0.6 : text.length === 3 ? 0.46 : 0.36;
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${Math.round(box * ratio)}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
  ctx.fillText(text, w / 2, w / 2 + box * 0.02);

  return canvas.toDataURL('image/png');
}
